import { useEffect, useMemo } from 'react';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { workspaceApi } from '@/api/workspace/index.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import { onSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import type { AppDispatch } from '@/store/store.ts';

let cleanup: (() => void) | undefined;

export function useSharedWorkspaceSignalrMessages() {
  const dispatch = useAppDispatch();
  const listenerService = useMemo(() => buildSharedWorkspaceSignalrListener(dispatch), []);

  useEffect(() => {
    if (!cleanup) {
      cleanup = () => {
        listenerService.removeListeners();
        cleanup = undefined;
      };

      listenerService.listen();
    }

    return cleanup;
  }, [listenerService]);
}

export interface SharedTabMessageData {
  sharedTabId: string;
}

function buildSharedWorkspaceSignalrListener(dispatch: AppDispatch) {
  const onSharedTabUpdatedMessage = ({ sharedTabId }: SharedTabMessageData) =>
    dispatch(workspaceApi.endpoints.getSharedTab.initiate(sharedTabId, { forceRefetch: true }));

  const onSharedTabRemovedMessage = ({ sharedTabId }: SharedTabMessageData) =>
    dispatch(onSharedTabRemoved(sharedTabId));

  const onSharedTabUnsubscribedMessage = ({ sharedTabId }: SharedTabMessageData) =>
    dispatch(workspaceApi.endpoints.getSharedTab.initiate(sharedTabId, { forceRefetch: true }));

  const signalRConnector = getSignalRConnector();

  const listen = () => {
    // the shared tab content has been updated
    signalRConnector.on('SharedTabUpdated', onSharedTabUpdatedMessage);
    // shared tab owner deleted the tab
    signalRConnector.on('SharedTabRemoved', onSharedTabRemovedMessage);
    // one user of the shared tab unsubscribe itself from the shared tab
    signalRConnector.on('SharedTabUnsubscribed', onSharedTabUnsubscribedMessage);
  };

  const removeListeners = () => {
    signalRConnector.off('SharedTabUpdated', onSharedTabUpdatedMessage);
    signalRConnector.off('SharedTabRemoved', onSharedTabRemovedMessage);
    signalRConnector.off('SharedTabUnsubscribed', onSharedTabUnsubscribedMessage);
  };

  return {
    listen,
    removeListeners,
  };
}
